import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router-dom";
import { appConstants } from "../_constants/app.constants";
import { isLikelyTournamentGuid, resolveTournamentIdFromPublicRoutes } from "../_utils/tournamentRouteUtils";
import { useConfirm } from "../common/ConfirmDialog";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Input,
  LoadingState,
  Toast,
  IconArrowLeft,
  IconPlus,
  IconTrophy,
} from "../ui";
import { directionLabel, formatGameValue } from "./gameUtils";

const selectClass =
  "w-full rounded-xl border border-arena/20 bg-white px-3 py-2 text-sm text-ink focus-ring";

function NewGameForm({ onCreate, busy }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("");
  const [higher, setHigher] = useState("true");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const ok = await onCreate({
      name: name.trim(),
      description: description.trim() || null,
      unit: unit.trim() || null,
      higherIsBetter: higher === "true",
    });
    if (ok) {
      setName("");
      setDescription("");
      setUnit("");
      setHigher("true");
    }
  };

  return (
    <Card>
      <CardHeader>
        <h2 className="font-display text-lg font-extrabold text-ink">Nuevo juego</h2>
        <p className="text-sm text-ink-muted">
          Define el reto (p. ej. «Toques al balón» o «Disparo más potente»), su unidad y cómo se gana.
        </p>
      </CardHeader>
      <CardBody>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          <Input label="Nombre" value={name} onChange={(e) => setName(e.target.value)} required />
          <Input
            label="Unidad (opcional)"
            placeholder="toques, km/h, s…"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
          <div className="sm:col-span-2">
            <Input
              label="Descripción (opcional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold text-ink-soft">Cómo se gana</span>
            <select className={selectClass} value={higher} onChange={(e) => setHigher(e.target.value)}>
              <option value="true">Mayor marca gana (toques, velocidad…)</option>
              <option value="false">Menor marca gana (tiempo más rápido…)</option>
            </select>
          </label>
          <div className="flex items-end">
            <Button type="submit" variant="gold" disabled={busy || !name.trim()} className="w-full">
              <IconPlus size={16} />
              Crear juego
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function EditGameForm({ game, onSave, onCancel, busy }) {
  const [name, setName] = useState(game.name ?? game.Name ?? "");
  const [description, setDescription] = useState(game.description ?? game.Description ?? "");
  const [unit, setUnit] = useState(game.unit ?? game.Unit ?? "");
  const [higher, setHigher] = useState(String(game.higherIsBetter ?? game.HigherIsBetter ?? true));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onSave({
          name: name.trim(),
          description: description.trim() || null,
          unit: unit.trim() || null,
          higherIsBetter: higher === "true",
        });
      }}
      className="grid gap-3 sm:grid-cols-2"
    >
      <Input label="Nombre" value={name} onChange={(e) => setName(e.target.value)} required />
      <Input label="Unidad" value={unit} onChange={(e) => setUnit(e.target.value)} />
      <div className="sm:col-span-2">
        <Input label="Descripción" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-semibold text-ink-soft">Cómo se gana</span>
        <select className={selectClass} value={higher} onChange={(e) => setHigher(e.target.value)}>
          <option value="true">Mayor marca gana</option>
          <option value="false">Menor marca gana</option>
        </select>
      </label>
      <div className="flex items-end gap-2">
        <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
          Guardar
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

function AddEntryRow({ teams, onAdd, busy }) {
  const [playerName, setPlayerName] = useState("");
  const [teamId, setTeamId] = useState("");
  const [value, setValue] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!playerName.trim() || value === "") return;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    const ok = await onAdd({
      playerName: playerName.trim(),
      teamId: teamId || null,
      value: numeric,
    });
    if (ok) {
      setPlayerName("");
      setTeamId("");
      setValue("");
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-2 border-t border-arena/10 bg-arena/[0.02] p-3 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end">
      <Input label="Jugador" value={playerName} onChange={(e) => setPlayerName(e.target.value)} required />
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-semibold text-ink-soft">Equipo (opcional)</span>
        <select className={selectClass} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">— Sin equipo —</option>
          {teams.map((t) => (
            <option key={t.id ?? t.Id} value={t.id ?? t.Id}>
              {t.name ?? t.Name}
            </option>
          ))}
        </select>
      </label>
      <Input
        label="Marca"
        type="number"
        step="any"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
      />
      <Button type="submit" variant="primary" disabled={busy || !playerName.trim() || value === ""}>
        <IconPlus size={16} />
        Añadir
      </Button>
    </form>
  );
}

export default function TournamentGamesAdminPage() {
  const { tournamentId: routeKey } = useParams();
  const confirm = useConfirm();
  const API = appConstants.API_URL;

  const [canonicalId, setCanonicalId] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [canOperate, setCanOperate] = useState(false);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [editingId, setEditingId] = useState(null);

  const resolveId = useCallback(async () => {
    const trimmed = routeKey ? String(routeKey).trim() : "";
    if (!trimmed) return null;
    if (isLikelyTournamentGuid(trimmed)) return trimmed;
    return await resolveTournamentIdFromPublicRoutes(API, trimmed);
  }, [routeKey, API]);

  const loadGames = useCallback(async (id) => {
    const res = await axios.get(`${API}/api/tournamentgames/${id}/games`);
    setGames(Array.isArray(res.data) ? res.data : []);
  }, [API]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const id = await resolveId();
        if (cancelled) return;
        if (!id) {
          setLoadError("No se encontró el torneo.");
          return;
        }
        setCanonicalId(id);

        const [accessRes, teamsRes] = await Promise.allSettled([
          axios.get(`${API}/api/tournaments/${id}/access`),
          axios.get(`${API}/api/tournamentteams/${id}/teams`),
        ]);
        if (cancelled) return;

        const accessData = accessRes.status === "fulfilled" ? accessRes.value.data : null;
        const manage = Boolean(accessData?.canManage);
        // Mesa/live operators can record marks but not create or edit games.
        const operate = Boolean(accessData?.canOperateLive) || manage;
        setCanManage(manage);
        setCanOperate(operate);
        if (teamsRes.status === "fulfilled" && Array.isArray(teamsRes.value.data)) {
          setTeams(teamsRes.value.data);
        }
        if (manage || operate) await loadGames(id);
      } catch {
        if (!cancelled) setLoadError("No se pudieron cargar los juegos.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolveId, loadGames, API]);

  const refresh = useCallback(async () => {
    if (canonicalId) await loadGames(canonicalId);
  }, [canonicalId, loadGames]);

  const apiError = (err) => err.response?.data?.message || err.response?.data?.Message || "Se produjo un error.";

  const handleCreate = async (payload) => {
    if (!canonicalId) return false;
    setBusy(true);
    setInfo("");
    setLoadError("");
    try {
      await axios.post(`${API}/api/tournamentgames`, { tournamentId: canonicalId, ...payload });
      setInfo("Juego creado.");
      await refresh();
      return true;
    } catch (err) {
      setLoadError(apiError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async (gameId, payload) => {
    setBusy(true);
    setInfo("");
    setLoadError("");
    try {
      await axios.put(`${API}/api/tournamentgames/${gameId}`, payload);
      setEditingId(null);
      setInfo("Juego actualizado.");
      await refresh();
    } catch (err) {
      setLoadError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteGame = async (game) => {
    const name = game.name ?? game.Name ?? "este juego";
    if (!(await confirm({
      title: "Eliminar juego",
      message: `¿Eliminar «${name}» y todas sus marcas? Esta acción no se puede deshacer.`,
      confirmText: "Eliminar",
      tone: "danger",
    }))) return;
    setBusy(true);
    setInfo("");
    setLoadError("");
    try {
      await axios.delete(`${API}/api/tournamentgames/${game.id ?? game.Id}`);
      setInfo("Juego eliminado.");
      await refresh();
    } catch (err) {
      setLoadError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAddEntry = async (gameId, payload) => {
    setBusy(true);
    setInfo("");
    setLoadError("");
    try {
      await axios.post(`${API}/api/tournamentgames/entries`, { gameId, ...payload });
      await refresh();
      return true;
    } catch (err) {
      setLoadError(apiError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    setBusy(true);
    setLoadError("");
    try {
      await axios.delete(`${API}/api/tournamentgames/entries/${entryId}`);
      await refresh();
    } catch (err) {
      setLoadError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const backTo = canonicalId ? `/tournamentactions/${canonicalId}` : `/tournamentactions/${routeKey}`;
  const sortedTeams = useMemo(
    () => [...teams].sort((a, b) => String(a.name ?? a.Name ?? "").localeCompare(String(b.name ?? b.Name ?? ""))),
    [teams]
  );

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 sm:py-10">
      <RouterLink to={backTo} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-arena">
        <IconArrowLeft size={16} />
        Volver al torneo
      </RouterLink>

      <header className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gold/15 text-gold-dim">
          <IconTrophy size={24} />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink sm:text-3xl">Gestionar juegos</h1>
          <p className="text-sm text-ink-muted">
            {canManage
              ? "Crea retos y registra las marcas de los participantes."
              : "Registra las marcas de los participantes en cada juego."}
          </p>
        </div>
      </header>

      {loading ? <LoadingState className="min-h-[30vh]" label="Cargando…" /> : null}

      {!loading && !canManage && !canOperate ? (
        <Toast kind="error">No tienes permisos para gestionar los juegos de este torneo.</Toast>
      ) : null}

      {!loading && (canManage || canOperate) ? (
        <div className="flex flex-col gap-6">
          {info ? <Toast kind="success">{info}</Toast> : null}
          {loadError ? <Toast kind="error">{loadError}</Toast> : null}

          {!canManage ? (
            <Toast kind="info">
              Tienes permiso de <strong>mesa</strong>: puedes registrar marcas en los juegos, pero no crear ni editar juegos.
            </Toast>
          ) : null}

          {canManage ? <NewGameForm onCreate={handleCreate} busy={busy} /> : null}

          {games.length === 0 ? (
            <EmptyState
              title="Aún no hay juegos"
              description={
                canManage
                  ? "Crea el primer reto arriba; luego podrás registrar marcas de los jugadores."
                  : "La organización todavía no ha creado juegos. Cuando los cree, podrás registrar marcas aquí."
              }
            />
          ) : (
            <div className="flex flex-col gap-5">
              {games.map((g) => {
                const gameId = g.id ?? g.Id;
                const unit = g.unit ?? g.Unit ?? "";
                const higher = g.higherIsBetter ?? g.HigherIsBetter ?? true;
                const entries = g.entries ?? g.Entries ?? [];
                const isEditing = editingId === gameId;
                return (
                  <Card key={gameId}>
                    <CardHeader>
                      {isEditing ? (
                        <EditGameForm
                          game={g}
                          busy={busy}
                          onCancel={() => setEditingId(null)}
                          onSave={(payload) => handleUpdate(gameId, payload)}
                        />
                      ) : (
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h2 className="font-display text-lg font-extrabold text-ink">{g.name ?? g.Name}</h2>
                              {unit ? <Badge variant="neutral">{unit}</Badge> : null}
                              <Badge variant={higher ? "success" : "info"}>{directionLabel(higher)}</Badge>
                            </div>
                            {g.description ?? g.Description ? (
                              <p className="mt-0.5 text-sm text-ink-muted">{g.description ?? g.Description}</p>
                            ) : null}
                          </div>
                          {canManage ? (
                            <div className="flex shrink-0 gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => setEditingId(gameId)} disabled={busy}>
                                Editar
                              </Button>
                              <Button type="button" size="sm" variant="outline" onClick={() => handleDeleteGame(g)} disabled={busy}>
                                Eliminar
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </CardHeader>
                    <CardBody className="p-0">
                      {entries.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="border-b border-arena/15 text-left text-xs font-bold uppercase tracking-wide text-ink-muted">
                                <th className="px-3 py-2 text-center">#</th>
                                <th className="px-3 py-2">Jugador</th>
                                <th className="px-3 py-2">Equipo</th>
                                <th className="px-3 py-2 text-right">Marca</th>
                                <th className="px-3 py-2" />
                              </tr>
                            </thead>
                            <tbody>
                              {entries.map((e) => (
                                <tr key={e.id ?? e.Id} className="border-b border-arena/8">
                                  <td className="px-3 py-2 text-center font-bold text-ink">{e.rank ?? e.Rank}</td>
                                  <td className="px-3 py-2 font-semibold text-ink">{e.playerName ?? e.PlayerName}</td>
                                  <td className="px-3 py-2 text-ink-muted">{e.teamName ?? e.TeamName ?? "—"}</td>
                                  <td className="px-3 py-2 text-right font-display font-extrabold text-arena">
                                    {formatGameValue(e.value ?? e.Value)}
                                    {unit ? <span className="ml-1 text-xs font-semibold text-ink-muted">{unit}</span> : null}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteEntry(e.id ?? e.Id)}
                                      disabled={busy}
                                      className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                                    >
                                      Quitar
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="px-4 py-4 text-sm text-ink-muted">Sin marcas todavía. Añade la primera abajo.</p>
                      )}
                      <AddEntryRow teams={sortedTeams} onAdd={(payload) => handleAddEntry(gameId, payload)} busy={busy} />
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
